import { useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Printer, ArrowRight, Loader2 } from "lucide-react";

const measureLabels = {
  shoulder: "Shoulder", bust: "Bust", hips: "Hips", length: "Length",
  sleeve_length: "Sleeve Len.", armhole: "Armhole", cuff: "Cuff",
  button_placement: "Button Pl.", sheila_length: "Sheila Len.",
};

export default function ConfirmationStep({ data, createdOrder, onSubmit, submitting, isEdit }) {
  const navigate = useNavigate();
  const grandTotal = (data.items || []).reduce((s, i) => s + i.quantity * i.price_per_piece, 0);

  if (createdOrder) {
    return (
      <div className="text-center py-8 space-y-6" data-testid="order-success">
        <div className="w-16 h-16 mx-auto border-2 border-[#4B6F44] flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-[#4B6F44]" />
        </div>
        <div>
          <h3 className="font-['Space_Mono'] text-2xl uppercase tracking-tight text-[#5C4D24]">Order Confirmed</h3>
          <p className="font-['Space_Mono'] text-lg text-[#9F7D23] mt-2">{createdOrder.order_number}</p>
        </div>
        <div className="flex justify-center gap-4 pt-4">
          <button onClick={() => navigate(`/orders/${createdOrder.id}`)} data-testid="view-order-btn" className="flex items-center gap-2 px-6 py-3 border border-[#9F7D23] text-[#9F7D23] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#9F7D23] hover:text-white transition-all">
            View Order <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate(`/orders/${createdOrder.id}`)} data-testid="print-order-btn" className="flex items-center gap-2 px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95">
            <Printer className="w-4 h-4" /> View & Print
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="confirmation-step">
      <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24] mb-1">Order Review</h3>
      <p className="text-sm text-[#8B7536] mb-6">Verify all details before confirming</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-[#D6D6CF] p-4">
          <h4 className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#9F7D23] mb-3">Customer</h4>
          <p className="text-sm text-[#5C4D24] font-medium">{data.customer.name}</p>
          <p className="text-xs text-[#8B7536]">{data.customer.contact}</p>
          <p className="text-xs text-[#8B7536]">{data.customer.address}</p>
        </div>
        <div className="border border-[#D6D6CF] p-4">
          <h4 className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#9F7D23] mb-3">Items ({(data.items || []).length})</h4>
          {(data.items || []).map((item, idx) => (
            <div key={idx} className="text-xs text-[#5C4D24] mb-1">
              <span className="font-medium">{item.garment_type}</span> - {item.fabric_name || "No fabric"} x{item.quantity} @ {item.price_per_piece} AED
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#D6D6CF] p-4 mb-6">
        <h4 className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#9F7D23] mb-3">Measurements (cm)</h4>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {Object.entries(measureLabels).map(([key, label]) => (
            <div key={key} className="text-center">
              <p className="font-['Space_Mono'] text-[9px] tracking-wider uppercase text-[#8B7536]">{label}</p>
              <p className="font-['Space_Mono'] text-lg text-[#5C4D24]">{data.measurements[key] || 0}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#D6D6CF] p-4 mb-8">
        <h4 className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#9F7D23] mb-3">Financial</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="font-['Space_Mono'] text-[9px] uppercase text-[#8B7536]">Total</p>
            <p className="text-[#5C4D24] font-bold">{grandTotal.toFixed(0)} AED</p>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[9px] uppercase text-[#8B7536]">Advance</p>
            <p className="text-[#5C4D24]">{data.financial.advance_paid} AED</p>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[9px] uppercase text-[#8B7536]">Balance</p>
            <p className="text-[#9F3A3A] font-bold">{(grandTotal - data.financial.advance_paid).toFixed(2)} AED</p>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[9px] uppercase text-[#8B7536]">Method</p>
            <p className="text-[#5C4D24] uppercase">{data.financial.payment_method}</p>
          </div>
        </div>
      </div>

      <Separator className="mb-6" />

      <div className="flex justify-end">
        <button onClick={onSubmit} disabled={submitting} data-testid="confirm-order-btn" className="flex items-center gap-2 px-10 py-4 bg-[#9F7D23] text-white font-['Space_Mono'] text-sm tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {isEdit ? "Update Order" : "Confirm & Place Order"}
        </button>
      </div>
    </div>
  );
}
